import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import Link from 'components/Link';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import {FC} from 'react';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {setUrl} from 'utils/request';

import {useDeleteField} from './services';
import {BackendData} from './types';

const LINKS = {
    seeDetails: 'https://help.makeleaps.jp/ja/articles/6920184',
};

const Page = () => {
    const seeDetailsLink = (
        <Link
            href={LINKS.seeDetails}
            isOpenInNewTab={true}
            key="see-details-link"
        >{t`See Details`}</Link>
    );
    return (
        <div>
            <DjangoMessages className="mb-2" />
            <NavigationTabs.DocumentSettings selection="extendedDataFields" />
            <SectionHeader
                subtitle={jt`Add your own custom fields to your MakeLeaps data. ${seeDetailsLink}`}
                title={t`Extended Data Fields`}
            />
            <DocumentExtendedData />
            <ItemExtendedData />
        </div>
    );
};

const DeleteFieldButton: FC<BackendData['data']['docFields'][number]> = (
    field,
) => {
    const deleteApi = useDeleteField(field.mid);

    const handleDelete = async () => {
        deleteApi.mutateAsync({mid: field.mid}).then(() => {
            setUrl(window.Urls.extendeddatafields_list_view());
        });
    };

    return (
        <TooltipWrapper message={field.permissions.canDelete.failureReason}>
            <Button
                aria-label={t`Delete`}
                disabled={!field.permissions.canDelete.allowed}
                icon="trash"
                kind="destructive"
                onClick={handleDelete}
                size="xs"
            ></Button>
        </TooltipWrapper>
    );
};

const DocumentExtendedData = () => {
    const {data} = useBackend<BackendData>();

    return (
        <div id="document">
            <SectionHeader
                button={{
                    disabled: !data.permissions.canCreate.allowed,
                    kind: 'primary',
                    label: t`Create`,
                    onClick: () => {
                        setUrl(
                            window.Urls.extendeddatafields_create_view(
                                'document',
                            ),
                        );
                    },
                }}
                className="pt-4"
                level={2}
                role="heading"
                title={t`Document Extended Data`}
                tooltipMessage={data.permissions.canCreate.failureReason}
            />
            {data.docFields.length === 0 && (
                <div className="mt-2 flex flex-col items-center gap-2 rounded border border-grey-300 p-4">
                    <div>{t`No fields found. Try making a new field.`}</div>
                    <TooltipWrapper
                        message={data.permissions.canCreate.failureReason}
                    >
                        <Button
                            aria-label={t`Create one now`}
                            disabled={!data.permissions.canCreate.allowed}
                            onClick={() => {
                                setUrl(
                                    window.Urls.extendeddatafields_create_view(
                                        'document',
                                    ),
                                );
                            }}
                            size="sm"
                        >
                            {t`Create one now`}
                        </Button>
                    </TooltipWrapper>
                </div>
            )}
            {data.docFields.length > 0 && (
                <TableStatic className="mt-2">
                    <TableStatic.Header>
                        <TableStatic.Row>
                            <TableStatic.Column>{t`Display Name`}</TableStatic.Column>
                            <TableStatic.Column>{t`Key`}</TableStatic.Column>
                            <TableStatic.Column>{t`Data Type`}</TableStatic.Column>
                            <TableStatic.Column></TableStatic.Column>
                        </TableStatic.Row>
                    </TableStatic.Header>
                    <TableStatic.Body>
                        {data.docFields.map((field) => (
                            <TableStatic.Row key={field.mid}>
                                <TableStatic.Cell>
                                    {field.displayName}
                                </TableStatic.Cell>
                                <TableStatic.Cell>{field.key}</TableStatic.Cell>
                                <TableStatic.Cell>
                                    {field.dataType}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <div className="float-right flex gap-2">
                                        <TooltipWrapper
                                            message={
                                                data.permissions.canEdit
                                                    .failureReason
                                            }
                                        >
                                            <Button
                                                disabled={
                                                    !data.permissions.canEdit
                                                        .allowed
                                                }
                                                kind="secondary"
                                                onClick={() => {
                                                    setUrl(
                                                        window.Urls.extendeddatafields_edit_view(
                                                            field.mid,
                                                        ),
                                                    );
                                                }}
                                                size="xs"
                                            >{t`Edit`}</Button>
                                        </TooltipWrapper>
                                        <DeleteFieldButton {...field} />
                                    </div>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))}
                    </TableStatic.Body>
                </TableStatic>
            )}
        </div>
    );
};

const ItemExtendedData = () => {
    const {data} = useBackend<BackendData>();

    return (
        <div id="lineitem">
            <SectionHeader
                button={{
                    disabled: !data.permissions.canCreate.allowed,
                    kind: 'primary',
                    label: t`Create`,
                    onClick: () => {
                        setUrl(
                            window.Urls.extendeddatafields_create_view(
                                'lineitem',
                            ),
                        );
                    },
                }}
                className="pt-4"
                level={2}
                role="heading"
                title={t`Line Item Extended Data`}
                tooltipMessage={data.permissions.canCreate.failureReason}
            />
            {data.itemFields.length === 0 && (
                <div className="mt-2 flex flex-col items-center gap-2 rounded border border-grey-300 p-4">
                    <div>{t`No fields found. Try making a new field.`}</div>
                    <TooltipWrapper
                        message={data.permissions.canCreate.failureReason}
                    >
                        <Button
                            aria-label={t`Create one now`}
                            disabled={!data.permissions.canCreate.allowed}
                            onClick={() => {
                                setUrl(
                                    window.Urls.extendeddatafields_create_view(
                                        'lineitem',
                                    ),
                                );
                            }}
                            size="sm"
                        >
                            {t`Create one now`}
                        </Button>
                    </TooltipWrapper>
                </div>
            )}
            {data.itemFields.length > 0 && (
                <TableStatic className="mt-2">
                    <TableStatic.Header>
                        <TableStatic.Row>
                            <TableStatic.Column>{t`Display Name`}</TableStatic.Column>
                            <TableStatic.Column>{t`Key`}</TableStatic.Column>
                            <TableStatic.Column>{t`Data Type`}</TableStatic.Column>
                            <TableStatic.Column></TableStatic.Column>
                        </TableStatic.Row>
                    </TableStatic.Header>
                    <TableStatic.Body>
                        {data.itemFields.map((field) => (
                            <TableStatic.Row key={field.mid}>
                                <TableStatic.Cell>
                                    {field.displayName}
                                </TableStatic.Cell>
                                <TableStatic.Cell>{field.key}</TableStatic.Cell>
                                <TableStatic.Cell>
                                    {field.dataType}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <div className="float-right flex gap-2">
                                        <TooltipWrapper
                                            message={
                                                data.permissions.canEdit
                                                    .failureReason
                                            }
                                        >
                                            <Button
                                                disabled={
                                                    !data.permissions.canEdit
                                                        .allowed
                                                }
                                                kind="secondary"
                                                onClick={() => {
                                                    setUrl(
                                                        window.Urls.extendeddatafields_edit_view(
                                                            field.mid,
                                                        ),
                                                    );
                                                }}
                                                size="xs"
                                            >{t`Edit`}</Button>
                                        </TooltipWrapper>
                                        <DeleteFieldButton {...field} />
                                    </div>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))}
                    </TableStatic.Body>
                </TableStatic>
            )}
        </div>
    );
};
export default Page;
